import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { PendingTask, registerPendingTaskDescBuilder } from './pending-task.js';

export const SET_EXAM_METADATA_TASK_CLASS = 'SetExamMetadataPendingTask';

/**
 * Options stored on the pending metadata task.
 * class is optional — only set when visible on the exam sheet.
 */
export interface SetExamMetadataTaskOptions {
  subject: string;
  topic: string;
  class?: string;
}

/**
 * Pending task created after the split agent processes the exam sheet.
 * Instructs the main agent to call set_exam_metadata with the extracted values.
 */
@Entity()
export class SetExamMetadataPendingTask extends PendingTask {
  static override _class = SET_EXAM_METADATA_TASK_CLASS;

  @Field(() => String)
  subject!: string;

  @Field(() => String)
  topic!: string;

  @Field(() => String)
  class: Maybe<string>;
}

/** Builds the raw document pushed to examState.pendingTasks in MongoDB. */
export function createSetExamMetadataPendingTask(options: SetExamMetadataTaskOptions): Record<string, unknown> {
  return {
    _class: SET_EXAM_METADATA_TASK_CLASS,
    subject: options.subject,
    topic: options.topic,
    class: options.class ?? null,
  };
}

// Register the desc builder for this task type (side-effect on import).
registerPendingTaskDescBuilder(SET_EXAM_METADATA_TASK_CLASS, task => {
  const t = task as unknown as Record<string, unknown>;
  const subject = String(t.subject ?? '');
  const topic = String(t.topic ?? '');
  const cls = typeof t.class === 'string' && t.class.trim() ? t.class.trim() : null;

  const classLine = cls ? `- Klasse: ${cls}` : `- Klasse: [NICHT AUF DEM KLAUSURBLATT GEFUNDEN]`;

  const action = cls
    ? `Rufe sofort set_metadata mit fach="${subject}", thema="${topic}" und klasse="${cls}" auf. Teile dem Nutzer im Tool-Log mit, dass die Metadaten gesetzt wurden. Weise ihn in der finalen Antwort darauf hin, dass er diese Informationen in der Seitenleiste bearbeiten kann.`
    : `Rufe set_metadata mit fach="${subject}" und thema="${topic}" auf (klasse weglassen). Erkläre im Tool-Log, dass Fach und Thema für die Generierung von Musterlösung und Bewertungsschema gespeichert wurden und die Klasse noch fehlt. Frage den Nutzer abschließend nach der Klasse.`;

  return `### PENDING TASK: Klausurmetadaten setzen [set_metadata]
Vom Klausurblatt wurden folgende Metadaten automatisch extrahiert:
- Fach: ${subject}
- Thema: ${topic}
${classLine}

Erforderliche Aktion: ${action}
Passe das Thema ${cls ? 'und die Klasse' : ''} der Sprache des Nutzers an.
Übernimm das Fach direkt.`;
});
